// Local deviations from upstream Untitled UI.
//
// 1. `CloseButton` accepts `onPress` only (react-aria), never `onClick`; upstream
//    passes `onClick`, which type-errors and is silently dropped at runtime.
// 2. The close button is labelled from `dismissLabel`. Upstream hardcodes
//    "Dismiss" there, so the accessible name could not be translated even when
//    the caller passed one.
// 3. Only `IconNotification` is kept. Upstream's `AvatarNotification` and
//    `ImageNotification`, and this one's `progress` branch, have no callers and
//    carried hardcoded English copy (`alt="Image Desktop"`, "% uploaded…") that
//    this app cannot ship — deleted rather than given an i18n surface no one uses.
// 4. The action row is not rendered when it would hold no buttons. Upstream
//    renders the row unconditionally, so a card with `hideDismissLabel` and no
//    `confirmLabel` still pays the column's `gap-3` beneath the text — 12px of
//    dead space that reads as a bottom-heavy, off-centre card.
// 5. An `action` slot takes a caller-rendered control in that row. Upstream
//    offers only `confirmLabel` + `onConfirm`, which cannot express a toast CTA
//    that carries its own handler and translated label; without it the toast
//    host had to render the CTA outside the card, detached from it.
//
// An `untitledui add notifications` reinstall restores all of it;
// `pnpm typecheck:changed` fails on deviation 1 when that happens.
// See docs/plans/2026-08-14-shadcn-migration-spike-findings.md Finding 0.
import type { FC, ReactNode } from 'react';
import { AlertCircle, CheckCircle, InfoCircle } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { CloseButton } from '@/components/base/buttons/close-button';
import { FeaturedIcon } from '@/components/foundations/featured-icon/featured-icon';
import { cx } from '@/lib/utils/cx';

const iconMap = {
  default: InfoCircle,
  brand: InfoCircle,
  gray: InfoCircle,
  error: AlertCircle,
  warning: AlertCircle,
  success: CheckCircle,
};

interface IconNotificationProps {
  title: string;
  description: string;
  /** A caller-rendered control for the action row, beside the label buttons. */
  action?: ReactNode;
  confirmLabel?: string;
  dismissLabel?: string;
  hideDismissLabel?: boolean;
  icon?: FC<{ className?: string }>;
  color?: 'default' | 'brand' | 'gray' | 'error' | 'warning' | 'success';
  onClose?: () => void;
  onConfirm?: () => void;
}

export const IconNotification = ({
  title,
  description,
  action,
  confirmLabel,
  dismissLabel = 'Dismiss',
  hideDismissLabel,
  icon,
  onClose,
  onConfirm,
  color = 'default',
}: IconNotificationProps) => {
  return (
    <div className="relative z-[var(--z-index)] flex max-w-full flex-col gap-4 rounded-xl bg-primary_alt p-4 shadow-lg ring ring-secondary_alt xs:w-[var(--width)] xs:flex-row">
      <FeaturedIcon
        icon={icon || iconMap[color]}
        color={color === 'default' ? 'gray' : color}
        theme={color === 'default' ? 'modern' : 'outline'}
        size="md"
      />

      <div className={cx('flex flex-1 flex-col gap-3 md:pr-8', color !== 'default' && 'md:pt-0.5')}>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-fg-primary">{title}</p>
          <p className="text-sm text-fg-secondary">{description}</p>
        </div>

        {(!hideDismissLabel || confirmLabel || action) && (
          <div className="flex gap-3">
            {!hideDismissLabel && (
              <Button onClick={onClose} size="sm" color="link-gray">
                {dismissLabel}
              </Button>
            )}
            {confirmLabel && (
              <Button onClick={onConfirm} size="sm" color="link-color">
                {confirmLabel}
              </Button>
            )}
            {action}
          </div>
        )}
      </div>

      <div className="absolute top-2 right-2 flex items-center justify-center">
        <CloseButton onPress={onClose} size="sm" label={dismissLabel} />
      </div>
    </div>
  );
};
